import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TweetCard from '@/components/TweetCard';
import FilterSidebar from '@/components/FilterSidebar';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { BarChart3, Upload, Loader2, LogOut, User as UserIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TweetViewerPage = () => {
  const [tweets, setTweets] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    tags: [], // Changed to array for multiple tags
    bookmarked: false,
    query: '',
    dataset: '', // Filter by dataset/file
    annotationStatus: 'all' // all, annotated, unannotated
  });
  const [totalTweets, setTotalTweets] = useState(0);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
      // Force reload to login anyway
      localStorage.removeItem('session_token');
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  useEffect(() => {
    fetchTweets();
  }, [filters]);

  const fetchTweets = async (maintainPosition = false) => {
    setLoading(true);
    
    // Save current tweet ID if we want to maintain position
    const currentTweetId = maintainPosition && tweets[currentIndex] ? tweets[currentIndex].id : null;
    
    try {
      const params = {
        skip: 0,
        limit: 10000, // Increased to handle very large datasets (up to 10k tweets)
        ...(filters.search && { search: filters.search }),
        ...(filters.tags.length > 0 && { tags: filters.tags.join(',') }),
        ...(filters.bookmarked && { bookmarked: true }),
        ...(filters.query && { query_filter: filters.query }),
        ...(filters.dataset && filters.dataset !== 'all' && { dataset: filters.dataset }),
        ...(filters.annotationStatus !== 'all' && { annotation_status: filters.annotationStatus })
      };

      const response = await axios.get(`${API}/tweets`, { 
        params,
        withCredentials: true 
      });
      
      const newTweets = response.data.tweets;
      setTweets(newTweets);
      setTotalTweets(response.data.total);
      
      // If we're maintaining position, try to find the same tweet
      if (currentTweetId && newTweets.length > 0) {
        const tweetIndex = newTweets.findIndex(t => t.id === currentTweetId);
        // If found, stay on that tweet; otherwise go to first tweet
        setCurrentIndex(tweetIndex >= 0 ? tweetIndex : 0);
      } else {
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Error fetching tweets:', error);
      // If access denied (403), redirect to login to trigger auth check
      if (error.response?.status === 403) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnnotationUpdate = (tweetId, annotation) => {
    setTweets(prevTweets =>
      prevTweets.map(tweet =>
        tweet.id === tweetId ? { ...tweet, annotation } : tweet
      )
    );
  };

  const handleNext = () => {
    if (currentIndex < tweets.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleRandom = () => {
    const randomIndex = Math.floor(Math.random() * tweets.length);
    setCurrentIndex(randomIndex);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading tweets...</p>
        </div>
      </div>
    );
  }

  if (tweets.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <p className="text-xl text-gray-600 mb-4">No tweets found matching your filters</p>
          <Button onClick={() => setFilters({ 
            search: '', 
            tags: [], 
            bookmarked: false, 
            query: '', 
            dataset: '', 
            annotationStatus: 'all' 
          })}>
            Clear Filters
          </Button>
        </div>
      </div>
    );
  }

  const currentTweet = tweets[currentIndex];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tweet-viewer-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tweet Viewer</h1>
            <p className="text-sm text-gray-600">Academic Discourse Analysis Tool</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="dashboard-button">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/upload')} data-testid="upload-more-button">
              <Upload className="w-4 h-4 mr-2" />
              Upload More
            </Button>
            {currentUser?.is_admin && (
              <Button variant="outline" onClick={() => navigate('/admin')} data-testid="admin-button" className="border-blue-600 text-blue-600">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin
              </Button>
            )}
            {currentUser && (
              <div className="flex items-center space-x-2 ml-2 pl-2 border-l border-gray-300">
                <div className="text-right mr-2">
                  <div className="text-sm font-medium text-gray-900">{currentUser.name}</div>
                  <div className="text-xs text-gray-500">{currentUser.email}</div>
                </div>
                <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button" className="border-red-300 text-red-600 hover:bg-red-50">
                  <LogOut className="w-4 h-4 mr-1" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <FilterSidebar filters={filters} setFilters={setFilters} onRefresh={() => fetchTweets(true)} />

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-4">
            {/* Annotation Status Filter & Counter */}
            <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-4">
              {/* Status Filter Buttons */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 mr-2">Show:</span>
                <button
                  onClick={() => setFilters({ ...filters, annotationStatus: 'all' })}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filters.annotationStatus === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="filter-all-button"
                >
                  All
                </button>
                <button
                  onClick={() => setFilters({ ...filters, annotationStatus: 'annotated' })}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filters.annotationStatus === 'annotated'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="filter-annotated-button"
                >
                  ✓ Annotated
                </button>
                <button
                  onClick={() => setFilters({ ...filters, annotationStatus: 'unannotated' })}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filters.annotationStatus === 'unannotated'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="filter-unannotated-button"
                >
                  ○ Unannotated
                </button>
              </div>

              {/* Tweet Counter */}
              <div className="text-sm text-gray-600" data-testid="tweet-counter">
                <span className="font-medium">
                  Tweet {currentIndex + 1} of {tweets.length}
                </span>
                {filters.annotationStatus === 'unannotated' && tweets.length > 0 && (
                  <span className="ml-2 text-orange-600 font-medium">
                    • {tweets.length} left to annotate
                  </span>
                )}
                {filters.annotationStatus === 'all' && totalTweets > tweets.length && (
                  <span className="ml-2 text-blue-600 text-xs">
                    (Showing first {tweets.length} of {totalTweets} total - use filters to narrow down)
                  </span>
                )}
              </div>
            </div>

            {/* Tweet Card */}
            <TweetCard
              tweet={currentTweet}
              onAnnotationUpdate={handleAnnotationUpdate}
            />

            {/* Navigation */}
            <Navigation
              onPrevious={handlePrevious}
              onNext={handleNext}
              onRandom={handleRandom}
              canGoPrevious={currentIndex > 0}
              canGoNext={currentIndex < tweets.length - 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TweetViewerPage;